import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme, alpha } from '@mui/material';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Typography,
  Avatar,
  Box,
  Chip,
  Button,
  Stack,
  Paper,
  IconButton,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  Payment as PaymentIcon,
  AttachMoney as AttachMoneyIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Project import
import { gridSpacing } from 'config.js';

// Styled Components
const StatCard = styled(Paper)(({ theme, color }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.spacing(2),
  background: `linear-gradient(135deg, ${alpha(color || theme.palette.primary.main, 0.08)} 0%, ${alpha(color || theme.palette.primary.main, 0.02)} 100%)`,
  border: `1px solid ${alpha(color || theme.palette.primary.main, 0.2)}`,
  transition: 'all 0.3s ease',
  height: '100%',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 8px 24px ${alpha(color || theme.palette.primary.main, 0.15)}`,
    borderColor: alpha(color || theme.palette.primary.main, 0.3)
  }
}));

const InfoCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0,0,0,0.12)'
  }
}));

const ViewPayroll = () => {
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [payrollData, setPayrollData] = useState(null);

  // Static data
  const mockData = {
    id: parseInt(id) || 1,
    employee: {
      name: 'Ahmad Rizki',
      photo: '',
      position: 'Software Developer',
      department: 'IT & Web Master',
      employeeId: 'EMP001',
      email: 'ahmad.rizki@eurekagroup.id',
      phone: '+62 812-3456-7890'
    },
    period: 'Januari 2024',
    basicSalary: 8000000,
    allowances: [
      { name: 'Tunjangan Transport', amount: 1000000 },
      { name: 'Tunjangan Makan', amount: 800000 },
      { name: 'Tunjangan Kesehatan', amount: 200000 }
    ],
    deductions: [
      { name: 'BPJS Kesehatan', amount: 200000 },
      { name: 'BPJS Ketenagakerjaan', amount: 300000 }
    ],
    bonuses: [
      { name: 'Bonus Performance', amount: 500000 }
    ],
    totalAllowances: 2000000,
    totalDeductions: 500000,
    totalBonuses: 500000,
    netSalary: 10000000,
    status: 'Paid',
    paymentDate: '2024-01-31',
    bankAccount: 'BCA - 1234567890',
    bankName: 'Bank Central Asia',
    notes: 'Pembayaran gaji bulanan sesuai dengan kontrak kerja'
  };

  useEffect(() => {
    setTimeout(() => {
      setPayrollData(mockData);
      setLoading(false);
    }, 500);
  }, [id]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return theme.palette.success.main;
      case 'pending':
        return theme.palette.warning.main;
      case 'cancelled':
        return theme.palette.error.main;
      default:
        return theme.palette.grey[500];
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!payrollData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">Data payroll tidak ditemukan</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <CardHeader
          avatar={
            <IconButton onClick={() => navigate('/payroll')} sx={{ bgcolor: 'primary.main', color: 'white' }}>
              <ArrowBackIcon />
            </IconButton>
          }
          title={
            <Typography variant="h4" sx={{ fontWeight: 700, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Detail Payroll
            </Typography>
          }
          action={
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/payroll/edit/${id}`)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => window.print()}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Print
              </Button>
            </Stack>
          }
        />
      </Card>

      <Grid container spacing={3}>
        {/* Left Column - Employee Info */}
        <Grid item xs={12} lg={4}>
          <InfoCard sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'primary.main',
                    fontSize: '2rem',
                    fontWeight: 700
                  }}
                >
                  {payrollData.employee.name.charAt(0)}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {payrollData.employee.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {payrollData.employee.position}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {payrollData.employee.department}
                  </Typography>
                </Box>
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={1.5}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="body2">{payrollData.employee.employeeId}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CalendarIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="body2">{payrollData.period}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <AccountBalanceIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="body2">{payrollData.bankAccount}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </InfoCard>

          {/* Status Card */}
          <InfoCard>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Status Pembayaran
              </Typography>
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Chip
                  label={payrollData.status}
                  sx={{
                    bgcolor: alpha(getStatusColor(payrollData.status), 0.1),
                    color: getStatusColor(payrollData.status),
                    border: `2px solid ${getStatusColor(payrollData.status)}`,
                    fontSize: '1rem',
                    fontWeight: 700,
                    height: 48,
                    px: 3,
                    '& .MuiChip-label': {
                      px: 2
                    }
                  }}
                  icon={payrollData.status === 'Paid' ? <CheckCircleIcon /> : <PendingIcon />}
                />
                {payrollData.paymentDate && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Dibayar pada: {new Date(payrollData.paymentDate).toLocaleDateString('id-ID')}
                  </Typography>
                )}
              </Box>
            </CardContent>
          </InfoCard>
        </Grid>

        {/* Middle Column - Salary Breakdown */}
        <Grid item xs={12} lg={8}>
          {/* Net Salary Card */}
          <InfoCard sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={2} mb={3}>
                <Box sx={{
                  width: 64,
                  height: 64,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <AttachMoneyIcon sx={{ color: theme.palette.success.main, fontSize: 32 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Gaji Bersih
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                    Rp {payrollData.netSalary.toLocaleString('id-ID')}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </InfoCard>

          <Grid container spacing={3}>
            {/* Allowances */}
            <Grid item xs={12} md={6}>
              <InfoCard>
                <CardHeader
                  title={
                    <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrendingUpIcon sx={{ color: 'success.main' }} />
                      Tunjangan
                    </Typography>
                  }
                />
                <Divider />
                <CardContent>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        {payrollData.allowances.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell sx={{ border: 'none', py: 1 }}>
                              <Typography variant="body2">{item.name}</Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ border: 'none', py: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                                + Rp {item.amount.toLocaleString('id-ID')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                        {payrollData.bonuses.map((item, index) => (
                          <TableRow key={`bonus-${index}`}>
                            <TableCell sx={{ border: 'none', py: 1 }}>
                              <Typography variant="body2">{item.name}</Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ border: 'none', py: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                                + Rp {item.amount.toLocaleString('id-ID')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell sx={{ border: 'none', pt: 2, fontWeight: 700 }}>
                            <Typography variant="body1" sx={{ fontWeight: 700 }}>
                              Total Tunjangan
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ border: 'none', pt: 2 }}>
                            <Typography variant="body1" sx={{ fontWeight: 700, color: 'success.main' }}>
                              + Rp {(payrollData.totalAllowances + payrollData.totalBonuses).toLocaleString('id-ID')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </InfoCard>
            </Grid>

            {/* Deductions */}
            <Grid item xs={12} md={6}>
              <InfoCard>
                <CardHeader
                  title={
                    <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrendingDownIcon sx={{ color: 'error.main' }} />
                      Potongan
                    </Typography>
                  }
                />
                <Divider />
                <CardContent>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        {payrollData.deductions.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell sx={{ border: 'none', py: 1 }}>
                              <Typography variant="body2">{item.name}</Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ border: 'none', py: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                                - Rp {item.amount.toLocaleString('id-ID')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell sx={{ border: 'none', pt: 2, fontWeight: 700 }}>
                            <Typography variant="body1" sx={{ fontWeight: 700 }}>
                              Total Potongan
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ border: 'none', pt: 2 }}>
                            <Typography variant="body1" sx={{ fontWeight: 700, color: 'error.main' }}>
                              - Rp {payrollData.totalDeductions.toLocaleString('id-ID')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </InfoCard>
            </Grid>

            {/* Salary Summary */}
            <Grid item xs={12}>
              <InfoCard>
                <CardHeader
                  title={
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Ringkasan Gaji
                    </Typography>
                  }
                />
                <Divider />
                <CardContent>
                  <TableContainer>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ border: 'none', py: 1.5 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              Gaji Pokok
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ border: 'none', py: 1.5 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              Rp {payrollData.basicSalary.toLocaleString('id-ID')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ border: 'none', py: 1.5 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: 'success.main' }}>
                              Total Tunjangan & Bonus
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ border: 'none', py: 1.5 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
                              + Rp {(payrollData.totalAllowances + payrollData.totalBonuses).toLocaleString('id-ID')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ border: 'none', py: 1.5 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500, color: 'error.main' }}>
                              Total Potongan
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ border: 'none', py: 1.5 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: 'error.main' }}>
                              - Rp {payrollData.totalDeductions.toLocaleString('id-ID')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ border: 'none', pt: 2, pb: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                              Gaji Bersih
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ border: 'none', pt: 2, pb: 1 }}>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                              Rp {payrollData.netSalary.toLocaleString('id-ID')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </InfoCard>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ViewPayroll;




